import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ApiClientError } from '../api/client'
import {
  getErrorHealth,
  getObservabilityAccess,
  getObservabilityClients,
  getObservabilityEvals,
  getObservabilityFreshness,
  getObservabilitySummary,
  getObservabilityTraces,
  getPlaybooks,
  getSettingsTokenSavings,
  getTokenSavingsFacets,
  type DiagnosticHealth,
  type ObservabilityClient,
  type ObservabilityFreshness,
  type ObservabilitySummaryResponse,
  type RunbookEntry,
  runObservabilityEval,
  TOKEN_SAVINGS_PERIODS,
  type TokenSavingsFacets,
  type TokenSavingsReport,
} from '../api/observability'
import { getTokenUsage, getTokenUsageModels, TOKEN_USAGE_PERIODS } from '../api/token-usage'
import TokenSavingsSummary from '../components/token-savings/TokenSavingsSummary'
import type { TokenUsageModelOption, TokenUsageSummary } from '../types/api'

type Tab =
  | 'overview'
  | 'clients'
  | 'errors'
  | 'retrieval'
  | 'evals'
  | 'access'
  | 'token-savings'
  | 'token-usage'

const VALID_TABS: readonly Tab[] = [
  'overview',
  'clients',
  'errors',
  'retrieval',
  'token-savings',
  'token-usage',
  'evals',
  'access',
]

function isTab(value: string | null): value is Tab {
  return VALID_TABS.includes(value as Tab)
}

const TABS: Array<{ id: Tab; label: string }> = [
  { id: 'overview', label: 'Overview' },
  { id: 'clients', label: 'Clients' },
  { id: 'errors', label: 'Errors' },
  { id: 'retrieval', label: 'Retrieval' },
  { id: 'token-savings', label: 'Token savings' },
  { id: 'token-usage', label: 'Token usage' },
  { id: 'evals', label: 'Evals' },
  { id: 'access', label: 'Access' },
]

function formatMs(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms)) return '—'
  if (ms < 1000) return `${Math.round(ms)} ms`
  return `${(ms / 1000).toFixed(1)} s`
}

function formatPct(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—'
  return `${(v * 100).toFixed(1)}%`
}

export default function ObservabilityPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const rawTab = searchParams.get('tab')
  const tab: Tab = isTab(rawTab) ? rawTab : 'overview'
  const setTab = useCallback(
    (next: Tab) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev)
          params.set('tab', next)
          return params
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )
  const [summary, setSummary] = useState<ObservabilitySummaryResponse | null>(null)
  const [clients, setClients] = useState<ObservabilityClient[]>([])
  const [freshness, setFreshness] = useState<ObservabilityFreshness[]>([])
  const [traces, setTraces] = useState<Record<string, unknown>[]>([])
  const [access, setAccess] = useState<Record<string, unknown>[]>([])
  const [evals, setEvals] = useState<Record<string, unknown>[]>([])
  const [evalRunning, setEvalRunning] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  // Errors tab state (observability-error-playbooks FR11).
  const [diagnostics, setDiagnostics] = useState<DiagnosticHealth[]>([])
  const [runbook, setRunbook] = useState<RunbookEntry | null>(null)
  const [traceRequestId, setTraceRequestId] = useState('')
  const [appliedRequestId, setAppliedRequestId] = useState('')
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const selectedCode = searchParams.get('code')
  const setSelectedCode = useCallback(
    (code: string | null) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev)
          params.set('tab', 'errors')
          if (code) params.set('code', code)
          else params.delete('code')
          return params
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  // Token savings tab state (Task 8): filters + report + per-tab loading/error/disabled.
  const [tsHours, setTsHours] = useState(24)
  const [tsProjectId, setTsProjectId] = useState('')
  const [tsClient, setTsClient] = useState('')
  const [tsTool, setTsTool] = useState('')
  const [tsReport, setTsReport] = useState<TokenSavingsReport | null>(null)
  const [tsLoading, setTsLoading] = useState(false)
  const [tsError, setTsError] = useState<string | null>(null)
  const [tsDisabled, setTsDisabled] = useState(false)
  const [tsFacets, setTsFacets] = useState<TokenSavingsFacets | null>(null)

  // Token usage tab state.
  const [tuDays, setTuDays] = useState(30)
  const [tuModel, setTuModel] = useState('')
  const [tuModels, setTuModels] = useState<TokenUsageModelOption[]>([])
  const [tuReport, setTuReport] = useState<TokenUsageSummary | null>(null)
  const [tuLoading, setTuLoading] = useState(false)
  const [tuError, setTuError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    setError(null)
    try {
      const [s, c, f, e] = await Promise.all([
        getObservabilitySummary(),
        getObservabilityClients(),
        getObservabilityFreshness(),
        getObservabilityEvals(),
      ])
      setSummary(s)
      setClients(c.clients)
      setFreshness(f.projects)
      setEvals(e.summaries)
      setEvalRunning(e.running)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  useEffect(() => {
    if (tab === 'access') {
      void getObservabilityAccess({ limit: 50 })
        .then((r) => setAccess(r.access))
        .catch(() => setAccess([]))
    }
  }, [tab])

  useEffect(() => {
    if (tab !== 'errors') return
    void getErrorHealth()
      .then((r) => setDiagnostics(r.diagnostics))
      .catch(() => setDiagnostics([]))
  }, [tab])

  useEffect(() => {
    if (tab !== 'errors') return
    void getObservabilityTraces({
      status: 'error',
      limit: 50,
      ...(selectedCode ? { diagnosticCode: selectedCode } : {}),
      ...(appliedRequestId ? { requestId: appliedRequestId } : {}),
    })
      .then((r) => setTraces(r.traces))
      .catch(() => setTraces([]))
  }, [tab, selectedCode, appliedRequestId])

  useEffect(() => {
    if (tab !== 'errors' || !selectedCode) {
      setRunbook(null)
      return
    }
    void getPlaybooks({ code: selectedCode })
      .then((r) => setRunbook(r.playbooks[0] ?? null))
      .catch(() => setRunbook(null))
  }, [tab, selectedCode])

  const refreshTokenSavings = useCallback(() => {
    setTsLoading(true)
    setTsError(null)
    setTsDisabled(false)
    if (!tsFacets) {
      getTokenSavingsFacets()
        .then((f) => setTsFacets(f))
        .catch(() => setTsFacets({ projects: [], clients: [], tools: [] }))
    }
    const projectId = tsProjectId ? Number.parseInt(tsProjectId, 10) : undefined
    getSettingsTokenSavings({
      hours: tsHours,
      ...(projectId !== undefined && Number.isFinite(projectId) ? { projectId } : {}),
      ...(tsClient ? { client: tsClient } : {}),
      ...(tsTool ? { tool: tsTool } : {}),
    })
      .then((r) => setTsReport(r))
      .catch((err) => {
        if (err instanceof ApiClientError && err.status === 403) {
          setTsDisabled(true)
        } else {
          setTsError(err instanceof Error ? err.message : String(err))
        }
      })
      .finally(() => setTsLoading(false))
  }, [tsHours, tsProjectId, tsClient, tsTool, tsFacets])

  useEffect(() => {
    if (tab === 'token-savings') refreshTokenSavings()
  }, [tab, refreshTokenSavings])

  const refreshTokenUsage = useCallback(async () => {
    setTuLoading(true)
    setTuError(null)
    try {
      if (tuModels.length === 0) {
        try {
          setTuModels(await getTokenUsageModels())
        } catch {
          setTuModels([])
        }
      }
      const report = await getTokenUsage(tuDays, tuModel || undefined)
      setTuReport(report)
    } catch (err) {
      setTuError(err instanceof Error ? err.message : String(err))
    } finally {
      setTuLoading(false)
    }
  }, [tuDays, tuModel, tuModels.length])

  useEffect(() => {
    if (tab === 'token-usage') refreshTokenUsage()
  }, [tab, refreshTokenUsage])

  const onRunEval = async () => {
    setEvalRunning(true)
    setError(null)
    try {
      await runObservabilityEval()
      const e = await getObservabilityEvals()
      setEvals(e.summaries)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setEvalRunning(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-harbor-text">Observability</h1>
          <p className="text-harbor-text-secondary mt-1">
            Requests, clients, retrieval quality, and gold-set evals
          </p>
        </div>
        <button
          type="button"
          onClick={() => void refresh()}
          className="px-3 py-1.5 text-sm rounded-lg bg-harbor-surface-raised text-harbor-text hover:bg-harbor-border"
        >
          Refresh
        </button>
      </div>

      {error && (
        <div className="rounded-lg border border-red-800 bg-red-950/40 px-4 py-3 text-sm text-red-300">
          {error}
        </div>
      )}

      <div className="flex flex-wrap gap-2 border-b border-harbor-border pb-2">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`px-3 py-1.5 text-sm rounded-lg ${
              tab === t.id
                ? 'bg-emerald-600/20 text-emerald-400'
                : 'text-harbor-text-secondary hover:text-harbor-text hover:bg-harbor-surface-raised'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && summary && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat label="In flight" value={String(summary.live.inFlight)} />
          <Stat label="HTTP errors" value={String(summary.live.httpErrors)} />
          <Stat label="MCP tool errors" value={String(summary.live.mcpToolErrors)} />
          <Stat label="p95 latency" value={formatMs(summary.latency.p95)} />
          <Stat label="p50" value={formatMs(summary.latency.p50)} />
          <Stat label="p99" value={formatMs(summary.latency.p99)} />
          <Stat label="MCP tools" value={String(summary.live.mcpToolTotal)} />
          <Stat label="MCP HTTP" value={String(summary.live.mcpHttpTotal)} />
        </div>
      )}

      {tab === 'clients' && (
        <div className="overflow-x-auto rounded-xl border border-harbor-border">
          <table className="min-w-full text-sm">
            <thead className="bg-harbor-surface text-harbor-text-secondary text-left">
              <tr>
                <th className="px-4 py-3">Key</th>
                <th className="px-4 py-3">HTTP</th>
                <th className="px-4 py-3">Tools</th>
                <th className="px-4 py-3">Errors</th>
                <th className="px-4 py-3">Last used</th>
              </tr>
            </thead>
            <tbody>
              {clients.map((c) => (
                <tr key={c.keyId} className="border-t border-harbor-border">
                  <td className="px-4 py-3 text-harbor-text">{c.keyName}</td>
                  <td className="px-4 py-3 text-harbor-text-secondary">{c.httpCalls}</td>
                  <td className="px-4 py-3 text-harbor-text-secondary">{c.toolCalls}</td>
                  <td className="px-4 py-3 text-red-300">{c.httpErrors + c.toolErrors}</td>
                  <td className="px-4 py-3 text-harbor-text-secondary">{c.lastUsedAt ?? '—'}</td>
                </tr>
              ))}
              {clients.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-harbor-text-muted text-center">
                    No MCP keys found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'errors' && (
        <div className="space-y-4" data-testid="errors-tab">
          <ErrorStats diagnostics={diagnostics} />
          <DependencyStrip diagnostics={diagnostics} />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <IssueList
              diagnostics={diagnostics}
              selectedCode={selectedCode}
              onSelect={setSelectedCode}
            />
            <RunbookPanel runbook={runbook} code={selectedCode} />
          </div>
          <TraceList
            traces={traces}
            traceRequestId={traceRequestId}
            onRequestIdChange={setTraceRequestId}
            onApplyRequestId={() => setAppliedRequestId(traceRequestId.trim())}
            copiedId={copiedId}
            onCopy={(id) => {
              setCopiedId(id)
              if (navigator.clipboard) void navigator.clipboard.writeText(id).catch(() => {})
            }}
          />
        </div>
      )}

      {tab === 'retrieval' && (
        <div className="overflow-x-auto rounded-xl border border-harbor-border">
          <table className="min-w-full text-sm">
            <thead className="bg-harbor-surface text-harbor-text-secondary text-left">
              <tr>
                <th className="px-4 py-3">Project</th>
                <th className="px-4 py-3">Indexed</th>
                <th className="px-4 py-3">Pending</th>
                <th className="px-4 py-3">Last indexed</th>
                <th className="px-4 py-3">Lag</th>
              </tr>
            </thead>
            <tbody>
              {freshness.map((p) => (
                <tr key={p.projectId} className="border-t border-harbor-border">
                  <td className="px-4 py-3 text-harbor-text">{p.projectName}</td>
                  <td className="px-4 py-3 text-harbor-text-secondary">
                    {p.indexedCount}/{p.fileCount}
                  </td>
                  <td className="px-4 py-3 text-harbor-text-secondary">{p.pendingCount}</td>
                  <td className="px-4 py-3 text-harbor-text-secondary">{p.lastIndexedAt ?? '—'}</td>
                  <td className="px-4 py-3 text-harbor-text-secondary">{formatMs(p.lagMs)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'token-savings' && (
        <div className="space-y-4" data-testid="token-savings-tab">
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-xs text-harbor-text-secondary flex flex-col gap-1">
              Time Period
              <select
                value={tsHours}
                onChange={(e) => setTsHours(Number(e.target.value))}
                className="px-2 py-1 rounded-lg bg-harbor-bg border border-harbor-border text-harbor-text text-sm"
              >
                {TOKEN_SAVINGS_PERIODS.map((p) => (
                  <option key={p.hours} value={p.hours}>
                    {p.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-xs text-harbor-text-secondary flex flex-col gap-1">
              Project
              <select
                value={tsProjectId}
                onChange={(e) => setTsProjectId(e.target.value)}
                className="px-2 py-1 rounded-lg bg-harbor-bg border border-harbor-border text-harbor-text text-sm"
              >
                <option value="">All projects</option>
                {(tsFacets?.projects ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-xs text-harbor-text-secondary flex flex-col gap-1">
              Client
              <select
                value={tsClient}
                onChange={(e) => setTsClient(e.target.value)}
                className="px-2 py-1 rounded-lg bg-harbor-bg border border-harbor-border text-harbor-text text-sm"
              >
                <option value="">All clients</option>
                {(tsFacets?.clients ?? []).map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-xs text-harbor-text-secondary flex flex-col gap-1">
              Tool
              <select
                value={tsTool}
                onChange={(e) => setTsTool(e.target.value)}
                className="px-2 py-1 rounded-lg bg-harbor-bg border border-harbor-border text-harbor-text text-sm"
              >
                <option value="">All tools</option>
                {(tsFacets?.tools ?? []).map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              onClick={refreshTokenSavings}
              className="px-3 py-1.5 text-sm rounded-lg bg-harbor-surface-raised text-harbor-text hover:bg-harbor-border"
            >
              Apply
            </button>
          </div>

          {tsDisabled && (
            <p className="text-sm text-harbor-text-muted">
              Token savings collection is disabled or you do not have access to this view.
            </p>
          )}
          {tsLoading && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-emerald-500" />
            </div>
          )}
          {!tsLoading && tsError && (
            <div className="rounded-lg border border-red-800 bg-red-950/40 px-4 py-3 text-sm text-red-300">
              {tsError}
            </div>
          )}
          {!tsLoading && !tsError && !tsDisabled && tsReport && (
            <TokenSavingsSummary report={tsReport} compact={false} />
          )}
        </div>
      )}

      {tab === 'token-usage' && (
        <div className="space-y-4" data-testid="token-usage-tab">
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-xs text-harbor-text-secondary flex flex-col gap-1">
              Time Period
              <select
                value={tuDays}
                onChange={(e) => setTuDays(Number(e.target.value))}
                className="px-2 py-1 rounded-lg bg-harbor-bg border border-harbor-border text-harbor-text text-sm"
              >
                {TOKEN_USAGE_PERIODS.map((p) => (
                  <option key={p.days} value={p.days}>
                    {p.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-xs text-harbor-text-secondary flex flex-col gap-1">
              Model
              <select
                value={tuModel}
                onChange={(e) => setTuModel(e.target.value)}
                className="px-2 py-1 rounded-lg bg-harbor-bg border border-harbor-border text-harbor-text text-sm"
              >
                <option value="">All models</option>
                {tuModels.map((m) => (
                  <option key={`${m.kind}:${m.name}`} value={m.name}>
                    {m.provider === 'unknown' ? m.name : `${m.name} — ${m.provider}`}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              onClick={refreshTokenUsage}
              className="px-3 py-1.5 text-sm rounded-lg bg-harbor-surface-raised text-harbor-text hover:bg-harbor-border"
            >
              Apply
            </button>
          </div>

          {tuLoading && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-emerald-500" />
            </div>
          )}
          {!tuLoading && tuError && (
            <div className="rounded-lg border border-red-800 bg-red-950/40 px-4 py-3 text-sm text-red-300">
              {tuError}
            </div>
          )}
          {!tuLoading && !tuError && tuReport && (
            <div className="space-y-6">
              {/* Summary cards */}
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                <div className="bg-harbor-surface border border-harbor-border rounded-xl p-4">
                  <p className="text-xs text-harbor-text-secondary">Total tokens</p>
                  <p className="text-2xl font-bold text-harbor-text mt-1">
                    {tuReport.totals.totalTokens.toLocaleString()}
                  </p>
                  <p className="text-xs text-harbor-text-muted mt-1">
                    {tuReport.totals.callCount.toLocaleString()} calls
                  </p>
                </div>
                <div className="bg-harbor-surface border border-harbor-border rounded-xl p-4">
                  <p className="text-xs text-harbor-text-secondary">Prompt tokens</p>
                  <p className="text-2xl font-bold text-harbor-text mt-1">
                    {tuReport.totals.promptTokens.toLocaleString()}
                  </p>
                </div>
                <div className="bg-harbor-surface border border-harbor-border rounded-xl p-4">
                  <p className="text-xs text-harbor-text-secondary">Completion tokens</p>
                  <p className="text-2xl font-bold text-harbor-text mt-1">
                    {tuReport.totals.completionTokens.toLocaleString()}
                  </p>
                </div>
                <div className="bg-harbor-surface border border-harbor-border rounded-xl p-4">
                  <p className="text-xs text-harbor-text-secondary">Models used</p>
                  <p className="text-2xl font-bold text-harbor-text mt-1">
                    {tuReport.perModel.length}
                  </p>
                </div>
              </div>

              {/* Per-model breakdown */}
              {tuReport.perModel.length > 0 && (
                <div className="bg-harbor-surface border border-harbor-border rounded-xl overflow-hidden">
                  <div className="p-4 border-b border-harbor-border">
                    <h3 className="text-sm font-semibold text-harbor-text">Per-model breakdown</h3>
                  </div>
                  <div className="divide-y divide-harbor-border">
                    {tuReport.perModel.map((m) => (
                      <div key={m.model} className="flex items-center justify-between px-4 py-3">
                        <span className="text-sm text-harbor-text">
                          <span className="font-mono">{m.model}</span>{' '}
                          <span className="text-xs text-harbor-text-muted">{m.provider}</span>
                        </span>
                        <div className="flex items-center gap-6 text-sm">
                          <span className="text-harbor-text-secondary">
                            {m.totalTokens.toLocaleString()} tokens
                          </span>
                          <span className="text-harbor-text-muted">
                            {m.callCount.toLocaleString()} calls
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Daily breakdown */}
              {tuReport.daily.length > 0 && (
                <div className="bg-harbor-surface border border-harbor-border rounded-xl overflow-hidden">
                  <div className="p-4 border-b border-harbor-border">
                    <h3 className="text-sm font-semibold text-harbor-text">Daily usage</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-harbor-surface/80 text-harbor-text-secondary text-left">
                        <tr>
                          <th className="px-4 py-2">Date</th>
                          <th className="px-4 py-2">Model</th>
                          <th className="px-4 py-2">Provider</th>
                          <th className="px-4 py-2 text-right">Prompt</th>
                          <th className="px-4 py-2 text-right">Completion</th>
                          <th className="px-4 py-2 text-right">Total</th>
                          <th className="px-4 py-2 text-right">Calls</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-harbor-border">
                        {tuReport.daily.map((d) => (
                          <tr key={`${d.day}-${d.model}`}>
                            <td className="px-4 py-2 text-harbor-text">{d.day}</td>
                            <td className="px-4 py-2 text-harbor-text-secondary font-mono text-xs">
                              {d.model}
                            </td>
                            <td className="px-4 py-2 text-harbor-text-secondary text-xs">
                              {d.provider}
                            </td>
                            <td className="px-4 py-2 text-right text-harbor-text-secondary">
                              {d.promptTokens.toLocaleString()}
                            </td>
                            <td className="px-4 py-2 text-right text-harbor-text-secondary">
                              {d.completionTokens.toLocaleString()}
                            </td>
                            <td className="px-4 py-2 text-right text-harbor-text font-medium">
                              {d.totalTokens.toLocaleString()}
                            </td>
                            <td className="px-4 py-2 text-right text-harbor-text-muted">
                              {d.callCount.toLocaleString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {tab === 'evals' && (
        <div className="space-y-4">
          <button
            type="button"
            disabled={evalRunning}
            onClick={() => void onRunEval()}
            className="px-4 py-2 rounded-lg bg-emerald-600 text-harbor-text text-sm font-medium disabled:opacity-50"
          >
            {evalRunning ? 'Running…' : 'Run gold-set eval'}
          </button>
          <div className="overflow-x-auto rounded-xl border border-harbor-border">
            <table className="min-w-full text-sm">
              <thead className="bg-harbor-surface text-harbor-text-secondary text-left">
                <tr>
                  <th className="px-4 py-3">Finished</th>
                  <th className="px-4 py-3">Project</th>
                  <th className="px-4 py-3">Queries</th>
                  <th className="px-4 py-3">Recall@5</th>
                  <th className="px-4 py-3">Recall@10</th>
                  <th className="px-4 py-3">MRR</th>
                </tr>
              </thead>
              <tbody>
                {evals.map((row, i) => (
                  <tr key={String(row['runId'] ?? i)} className="border-t border-harbor-border">
                    <td className="px-4 py-3 text-harbor-text-secondary">
                      {String(row['finishedAt'] ?? '—')}
                    </td>
                    <td className="px-4 py-3 text-harbor-text">
                      {String(row['projectName'] ?? '—')}
                    </td>
                    <td className="px-4 py-3 text-harbor-text-secondary">
                      {String(row['queryCount'] ?? '—')}
                    </td>
                    <td className="px-4 py-3 text-harbor-text-secondary">
                      {formatPct(Number(row['recallAt5']))}
                    </td>
                    <td className="px-4 py-3 text-harbor-text-secondary">
                      {formatPct(Number(row['recallAt10']))}
                    </td>
                    <td className="px-4 py-3 text-harbor-text-secondary">
                      {formatPct(Number(row['mrr']))}
                    </td>
                  </tr>
                ))}
                {evals.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-6 text-harbor-text-muted text-center">
                      No eval runs yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'access' && (
        <pre className="text-xs bg-harbor-bg border border-harbor-border rounded-xl p-4 overflow-auto max-h-[32rem] text-harbor-text-secondary">
          {JSON.stringify(access, null, 2)}
        </pre>
      )}
    </div>
  )
}

function StatusBadge({ status }: { status: DiagnosticHealth['status'] }) {
  const cls =
    status === 'active'
      ? 'bg-red-600/20 text-red-300'
      : status === 'resolved'
        ? 'bg-emerald-600/20 text-emerald-300'
        : 'bg-harbor-surface-raised text-harbor-text-muted'
  return (
    <span className={`px-2 py-0.5 text-xs rounded-full capitalize ${cls}`}>{status}</span>
  )
}

function ErrorStats({ diagnostics }: { diagnostics: DiagnosticHealth[] }) {
  const windowTotal = diagnostics.reduce((sum, d) => sum + d.countWindow, 0)
  const activeCount = diagnostics.filter((d) => d.status === 'active').length
  const resolvedCount = diagnostics.filter((d) => d.status === 'resolved').length
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      <Stat label="Window failures" value={String(windowTotal)} />
      <Stat label="Active issues" value={String(activeCount)} />
      <Stat label="Resolved issues" value={String(resolvedCount)} />
    </div>
  )
}

const DEP_GROUPS: Array<{ label: string; match: (d: DiagnosticHealth) => boolean }> = [
  { label: 'Embedding', match: (d) => d.errorClass === 'embedder_failure' },
  { label: 'Vector store', match: (d) => d.errorClass === 'vector_store_failure' },
  {
    label: 'Auth',
    match: (d) => d.errorClass === 'auth_failure' || d.errorClass === 'forbidden',
  },
  {
    label: 'MCP & API',
    match: (d) =>
      !['embedder_failure', 'vector_store_failure', 'auth_failure', 'forbidden'].includes(
        d.errorClass
      ),
  },
]

function worstStatus(items: DiagnosticHealth[]): DiagnosticHealth['status'] {
  if (items.some((d) => d.status === 'active')) return 'active'
  if (items.some((d) => d.status === 'resolved')) return 'resolved'
  return 'quiet'
}

function DependencyStrip({ diagnostics }: { diagnostics: DiagnosticHealth[] }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid="dependency-strip">
      {DEP_GROUPS.map((group) => (
        <span
          key={group.label}
          className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg bg-harbor-surface border border-harbor-border text-harbor-text"
        >
          {group.label}
          <StatusBadge status={worstStatus(diagnostics.filter(group.match))} />
        </span>
      ))}
    </div>
  )
}

function IssueList({
  diagnostics,
  selectedCode,
  onSelect,
}: {
  diagnostics: DiagnosticHealth[]
  selectedCode: string | null
  onSelect: (code: string | null) => void
}) {
  return (
    <div className="overflow-x-auto rounded-xl border border-harbor-border">
      <table className="min-w-full text-sm">
        <thead className="bg-harbor-surface text-harbor-text-secondary text-left">
          <tr>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3">Code</th>
            <th className="px-4 py-3">Window</th>
            <th className="px-4 py-3">Impact</th>
            <th className="px-4 py-3">Last failure</th>
          </tr>
        </thead>
        <tbody>
          {diagnostics.map((d) => (
            <tr
              key={d.diagnosticCode}
              onClick={() => onSelect(selectedCode === d.diagnosticCode ? null : d.diagnosticCode)}
              className={`border-t border-harbor-border cursor-pointer hover:bg-harbor-surface-raised ${
                selectedCode === d.diagnosticCode ? 'bg-emerald-600/10' : ''
              }`}
            >
              <td className="px-4 py-3">
                <StatusBadge status={d.status} />
                {d.reopenCount > 0 && (
                  <span className="ml-2 text-xs text-harbor-text-muted">
                    reopened ×{d.reopenCount}
                  </span>
                )}
              </td>
              <td className="px-4 py-3 font-mono text-xs text-harbor-text">{d.diagnosticCode}</td>
              <td className="px-4 py-3 text-harbor-text-secondary">{d.countWindow}</td>
              <td className="px-4 py-3 text-harbor-text-secondary text-xs">
                {d.affectedProjects} proj · {d.affectedClients} clients · {d.affectedTools} tools
              </td>
              <td className="px-4 py-3 text-harbor-text-secondary text-xs">
                {d.lastFailureAt ?? '—'}
              </td>
            </tr>
          ))}
          {diagnostics.length === 0 && (
            <tr>
              <td colSpan={5} className="px-4 py-6 text-harbor-text-muted text-center">
                No issues in this window
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

function RunbookSection({ title, items }: { title: string; items: string[] }) {
  if (items.length === 0) return null
  return (
    <div>
      <h4 className="text-xs font-semibold uppercase tracking-wide text-harbor-text-secondary">
        {title}
      </h4>
      <ul className="mt-1 list-disc pl-5 text-sm text-harbor-text space-y-1">
        {items.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    </div>
  )
}

function RunbookPanel({ runbook, code }: { runbook: RunbookEntry | null; code: string | null }) {
  if (!code) {
    return (
      <div className="rounded-xl border border-harbor-border bg-harbor-surface p-4 text-sm text-harbor-text-muted">
        Select an issue to see its runbook.
      </div>
    )
  }
  if (!runbook) {
    return (
      <div className="rounded-xl border border-harbor-border bg-harbor-surface p-4 text-sm text-harbor-text-muted">
        No runbook found for {code}.
      </div>
    )
  }
  return (
    <div className="rounded-xl border border-harbor-border bg-harbor-surface p-4 space-y-3" data-testid="runbook-panel">
      <div className="flex items-center gap-2">
        <h3 className="text-base font-semibold text-harbor-text">{runbook.title}</h3>
        <span className="px-2 py-0.5 text-xs rounded-full bg-harbor-surface-raised text-harbor-text-secondary">
          {runbook.severity}
        </span>
      </div>
      <p className="text-sm text-harbor-text-secondary">{runbook.impactSummary}</p>
      <RunbookSection title="Symptoms" items={runbook.symptoms} />
      <RunbookSection title="Checks" items={runbook.checks} />
      <RunbookSection title="Resolution" items={runbook.resolutionSteps} />
      <RunbookSection title="Verify" items={runbook.verifySteps} />
      <RunbookSection title="Escalate when" items={runbook.escalateWhen} />
      {runbook.links.length > 0 && (
        <div className="flex flex-wrap gap-2 pt-1">
          {runbook.links.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="text-xs text-emerald-400 hover:underline"
            >
              {link.label}
            </a>
          ))}
        </div>
      )}
    </div>
  )
}

function TraceList({
  traces,
  traceRequestId,
  onRequestIdChange,
  onApplyRequestId,
  copiedId,
  onCopy,
}: {
  traces: Record<string, unknown>[]
  traceRequestId: string
  onRequestIdChange: (value: string) => void
  onApplyRequestId: () => void
  copiedId: string | null
  onCopy: (id: string) => void
}) {
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-end gap-2">
        <label className="text-xs text-harbor-text-secondary flex flex-col gap-1">
          Filter by request ID
          <input
            value={traceRequestId}
            onChange={(e) => onRequestIdChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onApplyRequestId()
            }}
            placeholder="req-…"
            className="px-2 py-1 rounded-lg bg-harbor-bg border border-harbor-border text-harbor-text text-sm"
          />
        </label>
        <button
          type="button"
          onClick={onApplyRequestId}
          className="px-3 py-1.5 text-sm rounded-lg bg-harbor-surface-raised text-harbor-text hover:bg-harbor-border"
        >
          Apply
        </button>
      </div>
      <div className="overflow-x-auto rounded-xl border border-harbor-border">
        <table className="min-w-full text-sm">
          <thead className="bg-harbor-surface text-harbor-text-secondary text-left">
            <tr>
              <th className="px-4 py-3">Time</th>
              <th className="px-4 py-3">Tool</th>
              <th className="px-4 py-3">Code</th>
              <th className="px-4 py-3">Request ID</th>
            </tr>
          </thead>
          <tbody>
            {traces.map((trace, i) => {
              const requestId = String(trace['requestId'] ?? '')
              return (
                <tr key={String(trace['traceId'] ?? i)} className="border-t border-harbor-border">
                  <td className="px-4 py-3 text-harbor-text-secondary text-xs">
                    {String(trace['timestamp'] ?? '—')}
                  </td>
                  <td className="px-4 py-3 text-harbor-text">{String(trace['name'] ?? '—')}</td>
                  <td className="px-4 py-3 font-mono text-xs text-harbor-text">
                    {String(trace['diagnosticCode'] ?? trace['errorClass'] ?? '—')}
                  </td>
                  <td className="px-4 py-3">
                    {requestId ? (
                      <span className="inline-flex items-center gap-2 font-mono text-xs text-harbor-text-secondary">
                        {requestId}
                        <button
                          type="button"
                          onClick={() => onCopy(requestId)}
                          className="px-2 py-0.5 rounded bg-harbor-surface-raised text-harbor-text hover:bg-harbor-border"
                        >
                          {copiedId === requestId ? 'Copied' : 'Copy'}
                        </button>
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              )
            })}
            {traces.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-6 text-harbor-text-muted text-center">
                  No error traces found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-harbor-surface border border-harbor-border rounded-xl p-5">
      <p className="text-sm font-medium text-harbor-text-secondary">{label}</p>
      <p className="mt-2 text-2xl font-bold text-harbor-text">{value}</p>
    </div>
  )
}
